import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Square, Play, Pause, RotateCcw, Check, AlertCircle, ArrowLeft, Shuffle, MessageSquareQuote } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const VOICE_PROMPTS = [
  "What's the most spontaneous thing you've ever done?",
  "Describe your perfect Sunday from start to finish.",
  "What song can you not stop singing right now?",
  "Two truths and a lie — go!",
  "If we matched, where would our first date be?",
  "What's something you're weirdly passionate about?",
  "What always makes you laugh, no matter what?",
  "What's your go-to karaoke song and why?",
  "Tell me about the best meal you've ever had.",
  "What's one thing on your bucket list you'll do this year?"
];

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VoiceRecorder = ({ user, onComplete }) => {
  const navigate = useNavigate();
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioURL, setAudioURL] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [promptIndex, setPromptIndex] = useState(() => Math.floor(Math.random() * VOICE_PROMPTS.length));

  const shufflePrompt = () => {
    setPromptIndex((prev) => (prev + 1 + Math.floor(Math.random() * (VOICE_PROMPTS.length - 1))) % VOICE_PROMPTS.length);
  };
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.addEventListener('dataavailable', (event) => {
        audioChunksRef.current.push(event.data);
      });

      mediaRecorder.addEventListener('stop', () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/wav' });
        const audioURL = URL.createObjectURL(audioBlob);
        setAudioBlob(audioBlob);
        setAudioURL(audioURL);
      });

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);

    } catch (error) {
      console.error('Error accessing microphone:', error);
      toast.error('Could not access microphone');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const playAudio = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play();
        setIsPlaying(true);
      }
    }
  };

  const resetRecording = () => {
    setAudioBlob(null);
    setAudioURL(null);
    setIsPlaying(false);
    setRecordingTime(0);
  };

  const handleUpload = async () => {
    if (!audioBlob) return;

    setUploading(true);
    try {
      // Convert blob to base64
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      reader.onloadend = async () => {
        const base64Audio = reader.result;
        
        const response = await axios.post(
          `${API}/voice/upload`,
          { audio_data: base64Audio },
          { withCredentials: true }
        );

        if (response.data.success) {
          if (response.data.is_approved) {
            toast.success('Voice bio uploaded successfully!');
            onComplete();
            navigate('/');
          } else {
            toast.error(`Voice bio flagged: ${response.data.flagged_content?.join(', ')}`);
          }
        }
      };
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload voice bio');
    } finally {
      setUploading(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-purple-50">
      {/* Header */}
      <header className="glass border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/')}
            className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="card">
          <div className="text-center mb-8">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Record Your Voice Bio</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Share your personality, interests, and what makes you unique. 
              Be authentic and let your voice shine!
            </p>
          </div>

          {/* Recording Interface */}
          <div className="max-w-md mx-auto space-y-8">
            {/* Voice Prompt */}
            <div
              className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-100 rounded-2xl p-5"
              data-testid="voice-prompt-card"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-semibold text-purple-600 uppercase tracking-wide flex items-center">
                  <MessageSquareQuote className="w-4 h-4 mr-1.5" />
                  Need inspiration? Answer this:
                </span>
                <button
                  onClick={shufflePrompt}
                  className="flex items-center space-x-1 text-xs font-medium text-pink-500 hover:text-pink-600 transition-colors"
                  data-testid="shuffle-prompt-btn"
                >
                  <Shuffle className="w-3.5 h-3.5" />
                  <span>Shuffle</span>
                </button>
              </div>
              <p className="text-lg font-semibold text-gray-900" data-testid="voice-prompt-text">
                "{VOICE_PROMPTS[promptIndex]}"
              </p>
            </div>

            {/* Visual Feedback */}
            <div className="relative">
              <div className={`w-48 h-48 mx-auto rounded-full bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center transition-all ${
                isRecording ? 'recording-pulse scale-110' : ''
              }`}>
                <Mic className="w-24 h-24 text-white" />
              </div>
              {isRecording && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-56 h-56 rounded-full border-4 border-pink-300 animate-ping"></div>
                </div>
              )}
            </div>

            {/* Timer */}
            {isRecording && (
              <div className="text-center">
                <div className="text-4xl font-bold gradient-text">
                  {formatTime(recordingTime)}
                </div>
                <p className="text-sm text-gray-500 mt-1">Recording...</p>
              </div>
            )}

            {/* Controls */}
            <div className="space-y-4">
              {!audioBlob && (
                <button
                  onClick={isRecording ? stopRecording : startRecording}
                  className={`w-full py-4 rounded-full font-semibold text-lg flex items-center justify-center space-x-3 transition-all ${
                    isRecording
                      ? 'bg-red-500 hover:bg-red-600 text-white'
                      : 'btn-primary'
                  }`}
                  data-testid={isRecording ? 'stop-recording-btn' : 'start-recording-btn'}
                >
                  {isRecording ? (
                    <>
                      <Square className="w-6 h-6" />
                      <span>Stop Recording</span>
                    </>
                  ) : (
                    <>
                      <Mic className="w-6 h-6" />
                      <span>Start Recording</span>
                    </>
                  )}
                </button>
              )}

              {audioBlob && (
                <>
                  {/* Audio Player */}
                  <audio
                    ref={audioRef}
                    src={audioURL}
                    onEnded={() => setIsPlaying(false)}
                    className="hidden"
                  />

                  {/* Playback Controls */}
                  <div className="bg-gradient-to-r from-pink-50 to-purple-50 rounded-2xl p-6 space-y-4">
                    <div className="flex items-center justify-center space-x-4">
                      <button
                        onClick={playAudio}
                        className="w-16 h-16 rounded-full bg-pink-500 hover:bg-pink-600 text-white flex items-center justify-center transition-all"
                        data-testid="play-pause-btn"
                      >
                        {isPlaying ? (
                          <Pause className="w-8 h-8" />
                        ) : (
                          <Play className="w-8 h-8 ml-1" />
                        )}
                      </button>
                    </div>
                    <div className="text-center text-sm text-gray-600">
                      Duration: {formatTime(recordingTime)}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-4">
                    <button
                      onClick={resetRecording}
                      className="btn-secondary flex items-center justify-center space-x-2"
                      data-testid="reset-btn"
                    >
                      <RotateCcw className="w-5 h-5" />
                      <span>Re-record</span>
                    </button>
                    <button
                      onClick={handleUpload}
                      disabled={uploading}
                      className="btn-primary flex items-center justify-center space-x-2"
                      data-testid="upload-btn"
                    >
                      {uploading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      ) : (
                        <Check className="w-5 h-5" />
                      )}
                      <span>{uploading ? 'Uploading...' : 'Upload'}</span>
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Tips */}
            <div className="bg-blue-50 rounded-xl p-4 space-y-2">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-gray-700">
                  <p className="font-medium text-gray-900 mb-1">Tips for a great voice bio:</p>
                  <ul className="space-y-1 list-disc list-inside">
                    <li>Speak clearly and naturally</li>
                    <li>Share your interests and hobbies</li>
                    <li>Be authentic and positive</li>
                    <li>Keep it under 60 seconds</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default VoiceRecorder;